import React, { useState } from "react";
import { Text, Flex, Tag, Button } from "@jsq/ds/core";
import { Sizes } from "@jsq/ds/styles";
import { type ProcessingPaymentsWithConfirmationStatusQuery } from "~/graphql/supergraphTypes";
import { FieldRow } from "../../../utils/FieldRow";
import { formatFieldValue } from "../../../utils/formatFieldValue";
import * as styles from "./styles_vanilla.css";

type ProcessingPaymentWithProviderStatusType =
  ProcessingPaymentsWithConfirmationStatusQuery["processingPaymentsWithConfirmationStatus"][0]["payments"][0];

type ProviderStatusType = NonNullable<
  ProcessingPaymentWithProviderStatusType["adminProviderStatus"]
>;

export type FidesProviderStatusType = Extract<
  ProviderStatusType,
  { __typename?: "FidesProviderStatusDto" }
>;

export interface FidesProviderStatusProps {
  status: FidesProviderStatusType;
}

type Pain002StatusHistoryEntry = {
  timestamp: string;
  data: Record<string, unknown>;
};

type Pain002ParsedData = {
  data?: { TxSts?: string };
  statusHistory?: Record<string, Pain002StatusHistoryEntry>;
};

const PAIN002_STATUS_TAG_VARIANT: Record<string, "success" | "danger" | "neutral"> = {
  ACSC: "success",
  ACCC: "success",
  RJCT: "danger",
  ACSP: "neutral",
};

function getPain002TagVariant(statusCode: string): "success" | "danger" | "neutral" {
  return PAIN002_STATUS_TAG_VARIANT[statusCode] ?? "neutral";
}

function tryParsePain002Data(raw: string): Pain002ParsedData | null {
  try {
    return JSON.parse(raw) as Pain002ParsedData;
  } catch {
    return null;
  }
}

interface Pain002StatusDetailsProps {
  rawData: string;
  showRawData: boolean;
  onToggleRawData: () => void;
}

const Pain002StatusDetails: React.FC<Pain002StatusDetailsProps> = ({
  rawData,
  showRawData,
  onToggleRawData,
}) => {
  const parsed = tryParsePain002Data(rawData);
  const currentStatus = parsed?.data?.TxSts;
  const statusHistory = parsed?.statusHistory;

  return (
    <>
      {currentStatus && (
        <Flex gap={Sizes.SM} alignItems="center">
          <Text variant="strong-sm" style={{ minWidth: "180px" }}>
            Current Status:
          </Text>
          <Tag variant={getPain002TagVariant(currentStatus)}>{currentStatus}</Tag>
        </Flex>
      )}
      {statusHistory && Object.keys(statusHistory).length > 0 && (
        <Flex flexDirection="column" gap={Sizes.XS}>
          <Text variant="strong-sm">Status History:</Text>
          {Object.entries(statusHistory).map(([code, entry]) => (
            <Flex key={code} gap={Sizes.SM} alignItems="center">
              <Tag variant={getPain002TagVariant(code)}>{code}</Tag>
              <Text variant="regular-sm">{new Date(entry.timestamp).toLocaleString()}</Text>
            </Flex>
          ))}
        </Flex>
      )}
      <Flex flexDirection="column" gap={Sizes.XS}>
        <Button variant="neutral" onClick={onToggleRawData}>
          {showRawData ? "Hide Raw Data" : "Show Raw Data"}
        </Button>
        {showRawData && (
          <pre className={styles.preBlock}>
            {JSON.stringify(parsed ?? JSON.parse(rawData), null, 2)}
          </pre>
        )}
      </Flex>
    </>
  );
};

interface ConfirmationStatusRowProps {
  label: string;
  received: boolean;
}

const ConfirmationStatusRow: React.FC<ConfirmationStatusRowProps> = ({ label, received }) => (
  <Flex gap={Sizes.SM} alignItems="center">
    <Text variant="strong-sm" style={{ minWidth: "200px" }}>
      {label}
    </Text>
    <Tag variant={received ? "success" : "danger"}>{received ? "✓ Received" : "✗ Missing"}</Tag>
  </Flex>
);

const FidesProviderStatus: React.FC<FidesProviderStatusProps> = ({ status }) => {
  const [showSystemRawData, setShowSystemRawData] = useState(false);
  const [showDeliveryRawData, setShowDeliveryRawData] = useState(false);
  const [showFidesPayCnfRawData, setShowFidesPayCnfRawData] = useState(false);
  const [showPain002RawData, setShowPain002RawData] = useState(false);

  const {
    fidesConfirmationStatus,
    systemConfirmationProviderObject,
    deliveryConfirmationProviderObject,
    fidesPayCnfProviderObject,
    pain002ProviderObject,
  } = status;

  return (
    <div className={styles.providerStatusSection}>
      <Flex flexDirection="column" gap={Sizes.SM}>
        <Text variant="strong-md">FIDES Provider Status</Text>

        <ConfirmationStatusRow
          label="ACK1 (System Confirmation):"
          received={fidesConfirmationStatus.hasSystemConfirmation}
        />
        <ConfirmationStatusRow
          label="Delivery Confirmation:"
          received={fidesConfirmationStatus.hasDeliveryConfirmation}
        />
        <ConfirmationStatusRow
          label="FIDES Payment Confirmation:"
          received={fidesConfirmationStatus.hasFidesPayCnf}
        />
        <ConfirmationStatusRow
          label="pain.002 Status Report:"
          received={fidesConfirmationStatus.hasPain002}
        />

        {systemConfirmationProviderObject && (
          <Flex flexDirection="column" gap={Sizes.SM}>
            <Text variant="strong-md">ACK1 (System Confirmation) Provider Object:</Text>
            <FieldRow
              label="Provider Object DB ID:"
              value={formatFieldValue(systemConfirmationProviderObject.objectId)}
              labelMinWidth="180px"
            />
            <FieldRow
              label="Provider Object ID:"
              value={formatFieldValue(systemConfirmationProviderObject.providerObjectId)}
              labelMinWidth="180px"
            />
            <FieldRow
              label="Provider Object Type:"
              value={formatFieldValue(systemConfirmationProviderObject.providerObjectType)}
              labelMinWidth="180px"
            />
            {systemConfirmationProviderObject.data && (
              <Flex flexDirection="column" gap={Sizes.XS}>
                <Button variant="neutral" onClick={() => setShowSystemRawData((prev) => !prev)}>
                  {showSystemRawData ? "Hide Raw Data" : "Show Raw Data"}
                </Button>
                {showSystemRawData && (
                  <pre className={styles.preBlock}>
                    {JSON.stringify(JSON.parse(systemConfirmationProviderObject.data), null, 2)}
                  </pre>
                )}
              </Flex>
            )}
          </Flex>
        )}

        {deliveryConfirmationProviderObject && (
          <Flex flexDirection="column" gap={Sizes.SM}>
            <Text variant="strong-md">Delivery Confirmation Provider Object:</Text>
            <FieldRow
              label="Provider Object DB ID:"
              value={formatFieldValue(deliveryConfirmationProviderObject.objectId)}
              labelMinWidth="180px"
            />
            <FieldRow
              label="Provider Object ID:"
              value={formatFieldValue(deliveryConfirmationProviderObject.providerObjectId)}
              labelMinWidth="180px"
            />
            <FieldRow
              label="Provider Object Type:"
              value={formatFieldValue(deliveryConfirmationProviderObject.providerObjectType)}
              labelMinWidth="180px"
            />
            {deliveryConfirmationProviderObject.data && (
              <Flex flexDirection="column" gap={Sizes.XS}>
                <Button
                  variant="neutral"
                  onClick={() => setShowDeliveryRawData((prev) => !prev)}
                >
                  {showDeliveryRawData ? "Hide Raw Data" : "Show Raw Data"}
                </Button>
                {showDeliveryRawData && (
                  <pre className={styles.preBlock}>
                    {JSON.stringify(
                      JSON.parse(deliveryConfirmationProviderObject.data),
                      null,
                      2,
                    )}
                  </pre>
                )}
              </Flex>
            )}
          </Flex>
        )}

        {fidesPayCnfProviderObject && (
          <Flex flexDirection="column" gap={Sizes.SM}>
            <Text variant="strong-md">FIDES Payment Confirmation Provider Object:</Text>
            <FieldRow
              label="Provider Object DB ID:"
              value={formatFieldValue(fidesPayCnfProviderObject.objectId)}
              labelMinWidth="180px"
            />
            <FieldRow
              label="Provider Object ID:"
              value={formatFieldValue(fidesPayCnfProviderObject.providerObjectId)}
              labelMinWidth="180px"
            />
            <FieldRow
              label="Provider Object Type:"
              value={formatFieldValue(fidesPayCnfProviderObject.providerObjectType)}
              labelMinWidth="180px"
            />
            {fidesPayCnfProviderObject.data && (
              <Flex flexDirection="column" gap={Sizes.XS}>
                <Button
                  variant="neutral"
                  onClick={() => setShowFidesPayCnfRawData((prev) => !prev)}
                >
                  {showFidesPayCnfRawData ? "Hide Raw Data" : "Show Raw Data"}
                </Button>
                {showFidesPayCnfRawData && (
                  <pre className={styles.preBlock}>
                    {JSON.stringify(JSON.parse(fidesPayCnfProviderObject.data), null, 2)}
                  </pre>
                )}
              </Flex>
            )}
          </Flex>
        )}

        {pain002ProviderObject && (
          <Flex flexDirection="column" gap={Sizes.SM}>
            <Text variant="strong-md">pain.002 Provider Object:</Text>
            <FieldRow
              label="Provider Object DB ID:"
              value={formatFieldValue(pain002ProviderObject.objectId)}
              labelMinWidth="180px"
            />
            <FieldRow
              label="Provider Object ID:"
              value={formatFieldValue(pain002ProviderObject.providerObjectId)}
              labelMinWidth="180px"
            />
            <FieldRow
              label="Provider Object Type:"
              value={formatFieldValue(pain002ProviderObject.providerObjectType)}
              labelMinWidth="180px"
            />
            {pain002ProviderObject.data && (
              <Pain002StatusDetails
                rawData={pain002ProviderObject.data}
                showRawData={showPain002RawData}
                onToggleRawData={() => setShowPain002RawData((prev) => !prev)}
              />
            )}
          </Flex>
        )}
      </Flex>
    </div>
  );
};

export default FidesProviderStatus;
export { FidesProviderStatus, ConfirmationStatusRow };
export type { ConfirmationStatusRowProps };
